'use client'

import type { Zimmer, PricesBlock as PricesBlockProps, Preise } from '@/payload-types'

import React, { useRef } from 'react'
import RichText from '@/components/RichText'
import { motion, useScroll, useTransform } from 'motion/react'
import { useInView } from 'react-intersection-observer'
import AnimatedText from '@/components/Antonie/Components/animated-text'
import ZimmerPricingTable from '@/components/ZimmerPricingTable'
import { TypedLocale } from 'payload'

export const PricesBlockClient: React.FC<{
  title?: string | null
  introContent?: any
  globalPricing?: Preise
  locale: TypedLocale
}> = (props) => {
  const { title, introContent, globalPricing, locale } = props
  const sectionRef = useRef(null)
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  })

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '5%'])

  // Simplified title variants for better performance
  const titleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  // Simplified container variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  }

  // Simplified room variants
  const roomVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  // Group pricing entries by season
  const groupedPricing =
    globalPricing?.pricingTable?.reduce(
      (acc, entry) => {
        const seasonKey = entry.selectSeason || 'other'
        if (!acc[seasonKey]) {
          acc[seasonKey] = []
        }
        acc[seasonKey].push(entry)
        return acc
      },
      {} as Record<string, typeof globalPricing.pricingTable>,
    ) || {}

  // Seasonal color schemes
  const getSeasonalColors = (seasonKey: string) => {
    switch (seasonKey) {
      case 'sommer':
        return {
          bg: 'bg-gradient-to-br from-blue-50 to-gray-50',
          border: 'border-blue-200',
          shadow: 'shadow-blue-100',
        }
      case 'winter':
        return {
          bg: 'bg-gradient-to-br from-blue-50 to-gray-50',
          border: 'border-blue-200',
          shadow: 'shadow-blue-100',
        }
      default:
        return {
          bg: 'bg-white',
          border: 'border-gray-200',
          shadow: 'shadow-sm',
        }
    }
  }

  return (
    <motion.div ref={sectionRef} style={{ y }} className="container mx-auto px-6">
      {title && (
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={titleVariants}
          className="mb-20 text-center"
        >
          <AnimatedText
            text={title}
            type="h2"
            variant="simple"
            classNameText="text-2xl md:text-4xl"
            className="mb-8 font-heading text-2xl font-medium text-gray-900 md:text-4xl"
          />
          <div className="mx-auto mb-8 h-px w-16 bg-antonie-red" />
        </motion.div>
      )}

      {introContent && (
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={titleVariants}
          className="container mb-16 flex flex-col items-center"
        >
          <RichText
            className="mx-auto ms-0 w-full text-center"
            data={introContent}
            enableGutter={false}
          />
        </motion.div>
      )}

      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="space-y-16"
      >
        {Object.entries(groupedPricing).map(([seasonKey, entries]) => {
          const colors = getSeasonalColors(seasonKey)
          return (
            <motion.div
              key={seasonKey}
              variants={roomVariants}
              className={` border ${colors.bg} ${colors.border} p-8 ${colors.shadow}`}
            >
              <div className="mb-8 text-center">
                <h3 className="mb-4 font-serif text-2xl font-light text-gray-900">
                  {seasonKey === 'sommer'
                    ? locale === 'de'
                      ? 'Sommer'
                      : 'Summer'
                    : seasonKey === 'winter'
                      ? locale === 'de'
                        ? 'Winter'
                        : 'Winter'
                      : 'Saison'}
                </h3>
              </div>

              <ZimmerPricingTable pricingTable={entries || []} locale={locale} />
            </motion.div>
          )
        })}
      </motion.div>
    </motion.div>
  )
}
